import {
  getAuth,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithCustomToken,
  connectAuthEmulator
} from 'firebase/auth'
import { Magic } from 'magic-sdk'
import { OAuthExtension } from '@magic-ext/oauth'

import { getApp } from './firebase'
import { getUser } from './user'
import { MAGIC_APIKEY, FIREBASE_AUTH_SETTINGS, DAO } from '../config'
import { IS_DEV } from '../config'
import { grantWalletAuth, magicAuth, requestWalletAuth } from './functions'
import { walletSigature } from './wallet'

const FIREBASE_AUTH_EMAIL_KEY = 'emailForSignIn'
const MAGIC_USERREQUESTEDITEMAIL_ERROR = -10005
const auth = getAuth(getApp())

const magic = new Magic(MAGIC_APIKEY, {
  extensions: [new OAuthExtension()]
})

if (IS_DEV) {
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true })
}

// Get currently logged-in user
export const getAuthUser = () => auth.currentUser

export const logoutAuthUser = () => auth.signOut()

// Listen for auth changes
export const listenForAuthStateChanges = (
  setUser,
  signInCb = () => {},
  signOutCb = () => {}
) => {
  onAuthStateChanged(auth, async authUser => {
    if (authUser) {
      // TODO: cache auth state (authUser)
      // User is signed in, see docs for a list of available properties
      // https://firebase.google.com/docs/reference/js/firebase.User
      console.log('Auth: authUser:', authUser)
      const user = (await getUser(authUser.uid)).data()
      console.log('Auth: user:', user)
      setUser(user)
      signInCb(user)
    } else {
      // User is signed out
      console.log('Auth: unsetting authed user', authUser)
      // TODO: unset user
      setUser({})
      signOutCb()
    }
  })
}

export const emailLoginLink = email => {
  return sendSignInLinkToEmail(auth, email, FIREBASE_AUTH_SETTINGS)
    .then(() => {
      // The link was successfully sent. Inform the user.
      // Save the email locally so you don't need to ask the user for it again
      // if they open the link on the same device.
      return window.localStorage.setItem(FIREBASE_AUTH_EMAIL_KEY, email)
      // ...
    })
    .catch(error => console.error(error))
}

export const verifyEmailLogin = () => {
  if (isSignInWithEmailLink(auth, window.location.href)) {
    // Additional state parameters can also be passed via URL.
    // This can be used to continue the user's intended action before triggering
    // the sign-in operation.
    // Get the email if available. This should be available if the user completes
    // the flow on the same device where they started it.
    let email = window.localStorage.getItem(FIREBASE_AUTH_EMAIL_KEY)
    if (!email) {
      // User opened the link on a different device. To prevent session fixation
      // attacks, ask the user to provide the associated email again. For example:
      email = window.prompt('Please provide your email for confirmation')
    }
    // The client SDK will parse the code from the link for you.
    return signInWithEmailLink(auth, email, window.location.href).then(
      result => {
        console.log(result)
        // Clear email from storage.
        return window.localStorage.removeItem(FIREBASE_AUTH_EMAIL_KEY)
        // You can access the new user via result.user
        // Additional user info profile not available via:
        // result.additionalUserInfo.profile == null
        // You can check if the user is new or existing:
        // result.additionalUserInfo.isNewUser
      }
    )
  }
}

export const magicEmailLogin = async (email, editEmailCb) => {
  // User login with Magic link to get DID token
  try {
    var didToken = await magic.auth.loginWithMagicLink({ email })
  } catch (err) {
    if (err.code === MAGIC_USERREQUESTEDITEMAIL_ERROR) {
      editEmailCb && editEmailCb(err)
    } else {
      throw err
    }
  }
  // DID token is passed into the auth callable function
  let result = (await magicAuth({ didToken, dao: DAO })).data
  console.log('Magic auth result', result)
  // Firebase user access token is used to authenticate
  await signInWithCustomToken(auth, result.token)
}

export const magicOAuthLoginInit = (provider = 'discord', nextTo) =>
  magic.oauth.loginWithRedirect({
    provider,
    redirectURI: `${window.location.origin}/callback`,
    scope: ['email'] /* optional */
  })

export const walletAuth = async (wallet, provider, cb) => {
  const { account } = wallet
  console.log('Sending auth request for', account)
  const reqAuthRes = await requestWalletAuth({ account })
  const { nonce } = reqAuthRes?.data
  console.log('Request auth res:', reqAuthRes, nonce)
  const signature = await walletSigature(provider, account, nonce)
  console.log('Signed nonce:', signature)
  const grantAuthRes = await grantWalletAuth({ wallet, signature, dao: DAO })
  console.log('Grant auth res:', grantAuthRes)
  const { token } = grantAuthRes?.data
  await signInWithCustomToken(auth, token)

  cb && cb()
}
